import React, {FC, InputHTMLAttributes, useCallback, useEffect, useMemo, useState} from 'react'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import clsx from 'clsx'
import * as Yup from 'yup'
import {KTSVG, QUERIES, isNotEmpty} from '../../../../../../_metronic/helpers'
import {Task, ResponseTask} from '../core/_models'
import {useListView} from '../core/ListViewProvider'
import {Loading} from '../../../../../../_metronic/partials/components/loading/Loading'
import {cloneTask} from '../core/_requests'
import {getServerErrorMessage} from '../../../../../../_metronic/helpers/apiError'
import {
  getTaskAttachmentError,
  TASK_ATTACHMENT_ACCEPT,
} from '../../../../../../_metronic/helpers/taskAttachment'
import {useQueryResponse} from '../core/QueryResponseProvider'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {useSearchParams} from 'react-router-dom'
import {getProjectsTask} from '../../core/_requests'
import {Project} from '../../core/_models'
import {useAuth} from '../../../../auth'
import {Toast} from '../../../../../../_metronic/partials/widgets'
import {SelectAutoComplete} from '../../../../../../_metronic/partials/components/Select/SelectAutoComplete'
import {CreateProjectAndClient} from '../../../../../../_metronic/partials/modals/create-project-and-client/CreateProjectAndClientModal'
import {UsersSymbolGroup} from '../../projects-list/UsersSymbolGroup'
import {getUsersInstanceTask} from '../../../../../../_metronic/partials/modals/task/core/_requests'
import {getChecklistClone} from '../TaskChecklist/core/_requests'
import {TagsAutoComplete} from '../../../../../../_metronic/partials/modals/task/components/TagsAutoComplete'
import {Tag} from '../../../../../../_metronic/partials/modals/task/core/_models'
import {TaskSequenceResponsibles} from '../task-edit-modal/TaskSequenceResponsibles'
import {IconUserModel, UsersSymbolGroupColumn} from '../UsersSymbolGroupColumn'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {CKEditor} from '@ckeditor/ckeditor5-react'
import SourceEditingEditor from 'ckeditor5-build-classic-source-editing'
import StripColorsOnPaste from '../../../../../../_metronic/partials/components/rich-text-edit/plugins/StripColorsOnPaste'
import {AlertBoostrapButtons} from '../../../../../../_metronic/partials/widgets/alerts/AlertBoostrapButtons'
import {TaskDateTimeFields} from '../../../../../../_metronic/helpers/TaskDateTimeFields'
import {isValidTaskDateTimeRange, normalizeIncomingTaskDateTime, toDatePickerValue} from '../../../../../../_metronic/helpers/TaskDateTime'
import {
  getSprints,
  insertTaskInSprintId,
} from '../../../../sprints/components/kanban/core/_requests'
import {invalidateAfterTaskChange} from '../../../../../../_metronic/helpers/taskQueryInvalidation'

type Props = {
  isTaskLoading: boolean
  task: ResponseTask
  handleClose: () => void | undefined
}

interface checkProps extends InputHTMLAttributes<HTMLInputElement> {
  content: string
  check: boolean
  checked?: boolean
}

interface checkPropsFile extends InputHTMLAttributes<HTMLInputElement> {
  file: any
}

type CloneTaskFormValues = {
  task_id?: number
  creator_id?: string
  status?: string
  file: any[]
  name: string
  description: string
  begin_date?: string
  expected_date?: string
  project_id?: string
  checklist_concluded: Array<boolean | number>
  checklist_name: string[]
  users: any[]
  tags: any[]
  sequence_responsibles: any[]
}

const TaskCloneModalForm: FC<Props> = ({task, isTaskLoading, handleClose}) => {
  const parseChecklistChecked = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      return normalized === '1' || normalized === 'true'
    }
    return false
  }

  const intl = useIntl()
  const {setItemIdForUpdate} = useListView()
  const {refetch} = useQueryResponse()
  const [data, setData] = useState<Task>(task.tasks)
  const [selectedOptions, setSelectedOptions] = useState(data.tags)
  const queryClient = useQueryClient()
  const {query} = useQueryResponse()
  const [checks, setChecks] = useState<Array<checkProps>>([])
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const {currentUser, auth} = useAuth()
  const [showProjectAndClientModal, setShowProjectAndClientModal] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [clients, setClients] = useState<Project[]>([])
  const [name_input, setNameInput] = useState<string>(data.name?.toString()!)
  const [description_input, setDescriptionInput] = useState<string>(data.description?.toString()!)
  const [checksFile, setChecksFile] = useState<Array<checkPropsFile>>([])
  const [selectedProjectLabel, setSelectedProjectLabel] = useState<string>(task.tasks.project_name || '')
  const themeBackground = useMemo(
    () => (window.localStorage.getItem('kt_theme_mode_value') === 'dark' ? '#353b48' : '#fff'),
    []
  )

  const getCreatedTaskId = (response: any): number | null => {
    const idCandidate =
      response?.tasks?.[0]?.id ?? response?.task?.id ?? response?.data?.id ?? response?.id ?? null
    const parsed = Number(idCandidate)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }

  const getOpenSprintsOptions = async (): Promise<Record<string, string>> => {
    const response = await getSprints()
    const options: Record<string, string> = {}
    ;(response?.data || []).forEach((yearItem: any) => {
      ;(yearItem?.months || []).forEach((monthItem: any) => {
        ;(monthItem?.sprints || []).forEach((sprint: any) => {
          if (Number(sprint?.active) === 1 && sprint?.id) {
            const begin = sprint?.begin_date
            const end = sprint?.end_date
            options[String(sprint.id)] =
              begin && end
                ? `${begin} a ${end} - ${sprint?.sprint_name || `Sprint ${sprint.id}`}`
                : sprint?.sprint_name || `Sprint ${sprint.id}`
          }
        })
      })
    })
    return options
  }

  const handlePostCloneSprintLink = async (createdTaskId: number) => {
    const confirmation = await AlertBoostrapButtons.fire({
      title: 'Deseja vincular essa tarefa a uma sprint?',
      icon: 'question',
      background: themeBackground,
      showCancelButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
      reverseButtons: true,
    })

    if (!confirmation.isConfirmed) {
      return
    }

    // Fecha o modal de clone antes de abrir o select da sprint.
    // Caso contrário, o backdrop/stacking do modal pode ficar por cima e impedir cliques no `<select>`.
    handleClose()
    // SweetAlert2 renderiza logo em seguida; espere um pouco para o backdrop do modal anterior
    // sair por completo (senão o <select> pode ficar "inlicável").
    await new Promise((resolve) => setTimeout(resolve, 200))

    const openSprintsOptions = await getOpenSprintsOptions()
    if (Object.keys(openSprintsOptions).length === 0) {
      Toast.fire({
        icon: 'warning',
        title: 'Não há sprints abertas no momento.',
        customClass: {
          popup: 'bg-light-warning',
          title: 'text-warning',
        },
      })
      return
    }

    const sprintSelection = await AlertBoostrapButtons.fire({
      title: 'Selecione uma sprint aberta',
      input: 'select',
      inputOptions: openSprintsOptions,
      inputPlaceholder: 'Selecione uma sprint',
      inputAttributes: {
        // mantém o styling padrão do SweetAlert2 + aplica o mesmo padrão dos selects do sistema
        // (no sistema, selects costumam usar `form-select form-select-solid`).
        class: 'swal2-select form-select form-select-solid rounded fw-normal w-100',
        style:
          'width:100% !important; max-width:100% !important; box-sizing:border-box !important; border-radius: var(--bs-border-radius) !important;',
      },
      didOpen: (popup) => {
        const container = popup?.closest('.swal2-container') as HTMLElement | null
        if (container) container.style.zIndex = '99999'

        // Garanti que o <select> realmente recebe as classes/estilos (às vezes
        // o SweetAlert2 sobrescreve atributos do input) e evita overflow/corte.
        const selectEl = popup?.querySelector('select') as HTMLSelectElement | null
        if (selectEl) {
          selectEl.classList.add('form-select', 'form-select-solid', 'rounded', 'fw-normal', 'w-100')
          // Garante que a seta use a mesma cor do select do sistema.
          selectEl.style.setProperty('color', 'var(--kt-input-solid-color)', 'important')
          selectEl.style.setProperty('width', '100%', 'important')
          selectEl.style.setProperty('max-width', '100%', 'important')
          selectEl.style.setProperty('box-sizing', 'border-box', 'important')
          selectEl.style.setProperty('margin-left', '0', 'important')
          selectEl.style.setProperty('margin-right', '0', 'important')
          selectEl.style.setProperty('border-radius', 'var(--bs-border-radius)', 'important')

          // SweetAlert2 sobrescreve `background` do select e pode apagar o `background-image`
          // do Bootstrap. Reaplica o ícone de seta (mesma ideia do Bootstrap `form-select`).
          selectEl.style.setProperty(
            'appearance',
            'none',
            'important',
          )
          const computedColor = window.getComputedStyle(selectEl).color || '#000'
          const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='${computedColor}' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg>`
          const encodedSvg = encodeURIComponent(svg)
          selectEl.style.setProperty(
            'background-image',
            `url("data:image/svg+xml,${encodedSvg}")`,
            'important',
          )
          selectEl.style.setProperty('background-repeat', 'no-repeat', 'important')
          selectEl.style.setProperty('background-position', 'right 0.75rem center', 'important')
          selectEl.style.setProperty('background-size', '16px 12px', 'important')
          // Garante espaço para a seta não “encostar” no texto.
          selectEl.style.setProperty('padding-right', '2.5rem', 'important')
        }
      },
      showCancelButton: true,
      confirmButtonText: 'Vincular',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      background: themeBackground,
      inputValidator: (value) => (!value ? 'Selecione uma sprint para continuar' : null),
    })

    if (!sprintSelection.isConfirmed || !sprintSelection.value) {
      return
    }

    await insertTaskInSprintId(Number(sprintSelection.value), createdTaskId)
    Toast.fire({
      icon: 'success',
      title: 'Tarefa vinculada na sprint com sucesso.',
      customClass: {
        popup: 'bg-light-success',
        title: 'text-success',
      },
    })
  }

  const getSelectClients = useMutation(() => getProjectsTask(parseInt(task.tasks.project_id!)), {
    onSuccess: (response) => {
      setClients(response.data)
    },
  })

  useEffect(() => {
    getSelectClients.mutateAsync()
    getChecklistCost.mutateAsync()
    formik.setFieldValue('sequence_responsibles', data.sequence_responsibles)
    formik.setFieldValue('users', data.users)
  }, [])

  const handleCreateProjectAndClient = (newProjectName: string) => {
    setSearchParams({new_project_name: newProjectName})
    setShowProjectAndClientModal(true)
  }

  const addCheckFile = () => {
    setChecksFile([...checksFile, {file: ''}])
  }

  const removeCheck = (key: number) => {
    setChecks(checks.filter((value, index) => key !== index))
  }

  const updateData = (fieldsToUpdate: Partial<Task>) => {
    const updatedData = {...data, ...fieldsToUpdate}
    setData(updatedData)
  }

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const removeCheckFile = (key: number) => {
    setChecksFile(checksFile.filter((value, index) => key !== index))
  }

  const {data: dataConst} = useQuery(
    `${QUERIES.TASKS_LIST}-usersTask`,
    () => {
      return getUsersInstanceTask()
    },
    {
      cacheTime: 0,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  const initialValues: CloneTaskFormValues = {
    file: [],
    name: '',
    description: '',
    begin_date: normalizeIncomingTaskDateTime(data.begin_date, 'begin') || undefined,
    expected_date: normalizeIncomingTaskDateTime(data.expected_date, 'expected') || undefined,
    project_id: '',
    checklist_concluded: [],
    checklist_name: [],
    users: [],
    tags: [],
    sequence_responsibles: [],
  }

  const createAccountSchemas = [
    Yup.object().shape({
      name: Yup.string().required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
    }),
  ]

  const getChecklistCost = useMutation(() => getChecklistClone(task.tasks.id), {
    onSuccess: (response) => {
      const normalizedChecks = (response.data || []).map((item: any) => ({
        ...item,
        content: item?.content || item?.name || '',
        check: parseChecklistChecked(item?.check ?? item?.checked ?? item?.concluded),
      }))
      setChecks(normalizedChecks)
    },
  })

  const addCheck = () => {
    setChecks([...checks, {content: '', check: false}])
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index.toString())
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index)
    }
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    const draggedIndexStr = e.dataTransfer.getData('text/plain')
    const sourceIndex = parseInt(draggedIndexStr)
    if (isNaN(sourceIndex) || sourceIndex === dropIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }

    const items = [...checks]
    const draggedItem = items[sourceIndex]
    items.splice(sourceIndex, 1)
    items.splice(dropIndex, 0, draggedItem)
    setChecks(items)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const [currentSchema] = useState(createAccountSchemas[0])

  const formik = useFormik<CloneTaskFormValues>({
    initialValues: initialValues,
    validationSchema: currentSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.name)) {
          const response = await cloneTask({
            ...values,
            begin_date: values.begin_date || undefined,
            expected_date: values.expected_date || undefined,
          })
          const createdTaskId = getCreatedTaskId(response)

          Toast.fire({
            icon: 'success',
            title: intl.formatMessage(
              {id: 'ALERT.LABEL.SUCCESS_TASK_CLONE_ITEM'},
              {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASK'})}
            ),
            customClass: {
              popup: 'bg-light-success',
              title: 'text-success',
            },
          })

          if (createdTaskId) {
            await handlePostCloneSprintLink(createdTaskId)
          }

          invalidateAfterTaskChange(queryClient)
          queryClient.invalidateQueries(`${QUERIES.TASKS_LIST}-${query}`)
          queryClient.invalidateQueries(QUERIES.SPRINTS_LIST)
          queryClient.invalidateQueries(['calendar-tasks'])
          resetForm()

          const newParams = new URLSearchParams(searchParams)
          if (createdTaskId) {
            newParams.set('taskId', createdTaskId.toString())
          }
          newParams.delete('cloneTaskId')
          setSearchParams(newParams)
          handleClose()
        }
      } catch (ex) {
        // O motivo vem do servidor (anexo recusado, projeto fechado...); o texto padrão só entra
        // quando a resposta não trouxe nenhum.
        Toast.fire({
          icon: 'warning',
          title:
            getServerErrorMessage(ex) ??
            intl.formatMessage(
              {id: 'ALERT.LABEL.ERROR_TASK_CLONE_ITEM'},
              {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASK'})}
            ),
          timer: 8000,
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
      } finally {
        setSubmitting(false)
      }
    },
  })

  const submitForm = useCallback(
    async (e: any) => {
      e?.preventDefault?.()

      const payload = {
        ...formik.values,
        task_id: data.id,
        creator_id: currentUser?.id ? String(currentUser.id) : undefined,
        status: data.status,
        name: name_input,
        description: description_input,
        begin_date: formik.values.begin_date || undefined,
        expected_date: formik.values.expected_date || undefined,
        project_id: String(formik.values.project_id || task.tasks.project_id || ''),
        tags: selectedOptions || [],
        checklist_concluded: checks.map((check) => (parseChecklistChecked(check.check) ? 1 : 0)),
        checklist_name: checks.map((check) => (check.content || '').trim()),
        file: checksFile.map((file) => file.file).filter(Boolean),
      }

      if (
        payload.begin_date &&
        payload.expected_date &&
        !isValidTaskDateTimeRange(payload.begin_date, payload.expected_date)
      ) {
        Toast.fire({
          icon: 'warning',
          title: 'A data de início não pode ser maior que a data de vencimento.',
          customClass: {
            popup: 'bg-light-warning',
            title: 'text-warning',
          },
        })
        return
      }

      await formik.setValues(payload, false)
      await formik.submitForm()
    },
    [
      formik,
      data.id,
      data.status,
      currentUser?.id,
      name_input,
      description_input,
      selectedOptions,
      checks,
      checksFile,
      task.tasks.project_id,
    ]
  )

  useEffect(() => {
    formik.setFieldValue('task_id', data.id)
    formik.setFieldValue('creator_id', currentUser?.id ? String(currentUser.id) : undefined)
    formik.setFieldValue('status', data.status)
    formik.setFieldValue('name', name_input)
    formik.setFieldValue('description', description_input)
    formik.setFieldValue('project_id', task.tasks.project_id ? String(task.tasks.project_id) : '')
  }, [name_input, description_input, data.id, data.status, currentUser?.id])

  return (
    <>
      <div className='modal-content'>
        <form
          id='kt_modal_add_task_clone_form'
          className='form'
          onSubmit={(e) => e.preventDefault()}
          noValidate
        >
          <div className='modal-content'>
            <div className='modal-header border-0'>
              <div className='col-10 col-md-8'>
                <input
                  placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.TASK_TITLE'})}
                  type='text'
                  onKeyPress={(e) => {
                    e.key === 'Enter' && e.preventDefault()
                  }}
                  {...formik.getFieldProps('name')}
                  value={name_input}
                  onChange={(e: any) => setNameInput(e.target.value)}
                  className={clsx('form-control py-2 bg-transparent border-0 fs-1')}
                  autoComplete='off'
                />
              </div>

              <div className='col-md-3 d-none d-md-flex justify-content-end pe-4'>
                {formik.values.sequence_responsibles.length > 0 ? (
                  <UsersSymbolGroupColumn
                    users={formik.values.sequence_responsibles}
                    usersAll={dataConst?.users!}
                    isReadonly={true}
                  />
                ) : dataConst?.users?.length! > 0 ? (
                  <UsersSymbolGroup
                    users={dataConst?.users!}
                    classes='justify-content-end'
                    onChange={(users) => formik.setFieldValue('users', users)}
                  />
                ) : null}
              </div>

              <button
                className='btn btn-sm btn-icon btn-active-color-primary h-auto ms-4'
                onClick={(e) => {
                  if (e.detail === 0) return
                  handleClose()
                }}
              >
                <KTSVG
                  className='svg-icon-2x svg-icon-gray-800'
                  path='/media/icons/duotune/arrows/arr061.svg'
                />
              </button>
            </div>
            <div className='modal-body d-flex flex-column flex-lg-row justify-content-between'>
              <div className='col-lg-8 pe-md-5 pb-5 pb-lg-0 mb-15 mb-lg-0 border-bottom border-bottom-lg-0'>
                <ul
                  className='nav nav-stretch nav-line-tabs fw-bold border-transparent flex-nowrap '
                  role='tablist'
                >
                  <li className='nav-item'>
                    <a
                      className='nav-link cursor-pointer text-gray-900 active'
                      data-bs-toggle='tab'
                      href='#kt_task_description_pane_clone'
                    >
                      {intl.formatMessage({id: 'TASK.TABLE.DESCRIPTION'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a
                      className='nav-link cursor-pointer text-gray-900'
                      data-bs-toggle='tab'
                      href='#kt_task_attachments_pane_clone'
                    >
                      {intl.formatMessage({id: 'TASK.TABLE.ATTACHMENTS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a
                      className='nav-link cursor-pointer text-gray-900'
                      data-bs-toggle='tab'
                      href='#kt_task_checklist_pane_clone'
                    >
                      {intl.formatMessage({id: 'TASK.TABLE.CHECKLIST'})}
                    </a>
                  </li>
                  <li className='nav-item align-items-center'>
                    <a
                      className='nav-link cursor-pointer text-gray-900'
                      data-bs-toggle='tab'
                      href='#kt_task_responsible_sequence_pane_clone'
                    >
                      {intl.formatMessage({id: 'TASK.TABLE.RESPONSIBLE_SEQUENCE'})}
                    </a>
                    <OverlayTrigger
                      placement='top'
                      delay={{show: 50, hide: 100}}
                      overlay={
                        <Tooltip>
                          Crie uma sequência de responsáveis e organize a passagem de bastão da
                          tarefa. Assim, ao entregar uma parte, a tarefa será transferida de forma
                          automática para o próximo usuário.
                        </Tooltip>
                      }
                    >
                      <i className='fas fa-exclamation-circle ms-2 fs-7' />
                    </OverlayTrigger>
                  </li>
                </ul>
                <div className='tab-content pt-3 overflow-y-scroll' style={{maxHeight: '60vh'}}>
                  <div
                    id='kt_task_description_pane_clone'
                    className='task-ckeditor-container tab-pane fade show active'
                  >
                    <CKEditor
                      // @ts-ignore
                      editor={SourceEditingEditor}
                      disabled={formik.isSubmitting}
                      id='description'
                      onChange={(event: any, editor: any) => setDescriptionInput(editor.getData())}
                      config={{
                        extraPlugins: [StripColorsOnPaste],
                        toolbar: [
                          'undo',
                          'redo',
                          '|',
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          '|',
                          'link',
                          'blockQuote',
                          '|',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'uploadImage',
                        ],
                        simpleUpload: {
                          uploadUrl: `${process.env.REACT_APP_THEME_API_URL || process.env.REACT_APP_API_URL || ''}/api/upload`,
                          headers: {
                            Authorization: 'Bearer ' + auth?.api_token,
                          },
                        },
                        initialData: description_input,
                      } as any}
                    />
                  </div>
                  <div id='kt_task_attachments_pane_clone' className='tab-pane fade'>
                    {checksFile.map((check, key) => (
                      <div className='check d-flex align-items-center mb-4' key={key}>
                        <input
                          className='form-control form-control-solid py-2  rounded fw-normal'
                          type='file'
                          id='file'
                          accept={TASK_ATTACHMENT_ACCEPT}
                          disabled={formik.isSubmitting}
                          onKeyPress={(e) => {
                            e.key === 'Enter' && e.preventDefault()
                          }}
                          // value={''}

                          onChange={(e) => {
                            const selected = e.target.files?.[0]
                            const attachmentError = selected ? getTaskAttachmentError(selected) : null
                            if (attachmentError) {
                              Toast.fire({
                                icon: 'error',
                                title: attachmentError,
                                timer: 8000,
                                customClass: {
                                  popup: 'bg-light-danger',
                                  title: 'text-danger',
                                },
                              })
                              e.target.value = ''
                              return
                            }
                            setChecksFile(
                              checksFile.filter((c, index) => {
                                if (key === index) {
                                  if (e.target.files) {
                                    c.file = e.target.files[0]
                                  }
                                }
                                return c
                              })
                            )
                          }}
                        ></input>

                        <button onClick={() => removeCheckFile(key)} className='btn p-0 ms-5'>
                          <i className='bi bi-trash3-fill'></i>
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addCheckFile}
                      onKeyPress={(e) => {
                        e.key === 'Enter' && e.preventDefault()
                      }}
                      className='btn text-bold d-flex align-items-center p-0 mt-10'
                    >
                      <div className='d-flex align-items-center justify-content-center w-15px h-15px bg-gray-900 me-3'>
                        <i className='bi bi-plus text-gray-100 fs-5 p-0'></i>
                      </div>
                      {intl.formatMessage({id: 'GENERAL.ADD_ITEM'})}
                    </button>
                  </div>

                  <div id='kt_task_checklist_pane_clone' className='tab-pane fade'>
                    <div className='checklist pt-6'>
                      <div className='checklist-items'>
                      {checks?.map((x, key) => {
                        return (
                          <div
                            key={key}
                            className={`check d-flex align-items-center ${
                              draggedIndex === key ? 'dragging' : ''
                            } ${dragOverIndex === key ? 'drag-over' : ''}`}
                            onDragOver={(e) => handleDragOver(e, key)}
                            onDrop={(e) => handleDrop(e, key)}
                          >
                            <div
                              className='drag-handle me-3'
                              draggable
                              onDragStart={(e) => handleDragStart(e, key)}
                              onDragEnd={handleDragEnd}
                              title='Arrastar para reordenar'
                            >
                              <i className='bi bi-grip-vertical text-gray-500'></i>
                            </div>

                            <div className='flex-grow-1 d-flex align-items-center'>
                            <input
                              className='form-check-input me-3'
                              type='checkbox'
                              id={key.toString()}
                              {...formik.getFieldProps('checklist_concluded')}
                              onKeyPress={(e) => {
                                e.key === 'Enter' && e.preventDefault()
                              }}
                              defaultChecked={false}
                              checked={Boolean(x.check)}
                              onChange={(e) => {
                                const newChecks = [...checks]
                                newChecks[key] = {...newChecks[key], check: e.target.checked}
                                setChecks(newChecks)
                              }}
                            />

                            <input
                              className='form-control border-0 bg-transparent p-0'
                              type='text'
                              id={key.toString()}
                              onKeyPress={(e) => {
                                e.key === 'Enter' && e.preventDefault()
                              }}
                              placeholder={intl.formatMessage({id: 'CHECKLIST.NAME_NEW'})}
                              {...formik.getFieldProps('checklist_name')}
                              value={x.content}
                              onChange={(e) => {
                                const newChecks = [...checks]
                                newChecks[key] = {...newChecks[key], content: e.target.value}
                                setChecks(newChecks)
                              }}
                              style={{flex: 1}}
                            ></input>
                            </div>

                            <button
                              onClick={() => removeCheck(key)}
                              className='btn p-0 ms-3'
                              style={{flexShrink: 0}}
                            >
                              <i className='bi bi-trash3-fill text-danger'></i>
                            </button>
                          </div>
                        )
                      })}
                      </div>
                      <button
                        onClick={addCheck}
                        onKeyPress={(e) => {
                          e.key === 'Enter' && e.preventDefault()
                        }}
                        className='btn text-bold d-flex align-items-center p-0 mt-10'
                      >
                        <div className='d-flex align-items-center justify-content-center w-15px h-15px bg-gray-900 me-3'>
                          <i className='bi bi-plus text-gray-100 fs-5 p-0'></i>
                        </div>
                        {intl.formatMessage({id: 'GENERAL.ADD_ITEM'})}
                      </button>
                    </div>
                  </div>
                  {dataConst?.users && (
                    <TaskSequenceResponsibles
                      tabId='kt_task_responsible_sequence_pane_clone'
                      isCreate={true}
                      formikSetFieldValue={formik.setFieldValue}
                      users={dataConst?.users!}
                      responsibles={formik.values.sequence_responsibles}
                      isClone={true}
                    />
                  )}
                </div>
              </div>

              <div className='col-lg-4 border-start-lg ps-lg-10'>
                <div className='border-bottom pb-8'>
                  <div className='d-flex align-items-center mb-3'>
                    <i className='bi bi-calendar-range me-2 text-primary fs-5'></i>
                    <span className='fw-bold text-dark'>Datas</span>
                  </div>

                  <TaskDateTimeFields
                    beginDateTime={formik.values.begin_date}
                    expectedDateTime={formik.values.expected_date}
                    onBeginDateTimeChange={(value) => formik.setFieldValue('begin_date', value || undefined)}
                    onExpectedDateTimeChange={(value) => formik.setFieldValue('expected_date', value || undefined)}
                    beginPlaceholder={intl.formatMessage({id: 'FORM.INPUT.NAME.BEGIN_DATE'})}
                    expectedPlaceholder={intl.formatMessage({id: 'TASK.LABEL.ESTIMATED_DEADLINE'})}
                    minBeginDate={new Date()}
                    minExpectedDate={toDatePickerValue(formik.values.begin_date) || new Date()}
                  />

                  <div className='d-flex mb-4'>
                    <span className='fw-bold text-dark me-1'>
                      {intl.formatMessage({id: 'TASK.LABEL.CREATOR_DEADLINE'})} {data.creator_name}
                    </span>
                  </div>
                </div>

                <div className='pt-10'>
                  <div>
                    <label className='fw-bold text-dark me-1'>Tags:</label>
                    <TagsAutoComplete
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                    />
                  </div>
                </div>

                <div className='pt-4'>
                  <div>
                    <span className='fw-bold text-dark me-1'>
                      {intl.formatMessage({id: 'TASK.LABEL.PROJECT'})}
                    </span>

                    <SelectAutoComplete
                      endPoint='projetos'
                      value={{value: formik.values.project_id, label: selectedProjectLabel}}
                      placeholder={intl.formatMessage({id: 'FORM.INPUT.TOOLTIP.PROJECT'})}
                      onChange={(e) => {
                        formik.setFieldValue('project_id', e?.value ? String(e.value) : '')
                        setSelectedProjectLabel(e?.label || '')
                      }}
                      onCreateOption={(inputValue) => handleCreateProjectAndClient(inputValue)}
                    />
                  </div>
                </div>

                <div className='text-center mt-20'>
                  <a onClick={submitForm} className='btn btn-primary btn-sm py-2 px-8' href='#'>
                    {intl.formatMessage({id: 'GENERAL.CLONE_TASK'})}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </form>
        {(formik.isSubmitting || isTaskLoading) && <Loading />}
      </div>

      <CreateProjectAndClient
        show={showProjectAndClientModal}
        handleClose={() => setShowProjectAndClientModal(false)}
      />
    </>
  )
}

export {TaskCloneModalForm}
